/**
 * Supported locales for overflow service configuration.
 */
export const SUPPORTED_OVERFLOW_LOCALES = ['en-US', 'es-US', 'en-CA', 'fr-CA', 'en-AU', 'en-SG'];

export interface OverflowServiceConfig {
    esMpvId: string;
    designerGroupOption?: { key: string; value: string };
}

/**
 * Minimal typed subset of an ExpertService from the graph.
 * Used by helper functions that need to check overflow eligibility
 * without depending on the full generated ExpertService type.
 */
export interface OverflowExpertService {
    isOverflowEligible: boolean;
    serviceProduct: { mpvId: string };
}

/**
 * Determines overflow service configuration for the Header component.
 * Uses the `isOverflowEligible` field from the graph to determine eligibility
 * instead of relying on a static mapping.
 * Only applies the overflow mapping for supported locales.
 *
 * @param expertService - The expert service object from graph data
 * @param locale - The current locale
 * @param reviewMethod - The selected review method (e.g. 'Standard', 'DesignLive')
 * @returns OverflowServiceConfig with esMpvId and designerGroupOption (if applicable), or null if not eligible
 */

// TODO: separate overflow from the intention of this function
// which is whether or not to provide options
export function getOverflowServiceConfig(
    expertService: OverflowExpertService | undefined,
    locale: string,
    reviewMethod: string
): OverflowServiceConfig | null {
    // Only apply overflow mapping for supported locales
    if (!SUPPORTED_OVERFLOW_LOCALES.includes(locale)) {
        return null;
    }

    if (reviewMethod !== 'Standard') {
        return null;
    }

    // TODO: rename isOverflowEligible to "isNewProductModel"
    if (!expertService?.isOverflowEligible) {
        return null;
    }

    const config: OverflowServiceConfig = {
        esMpvId: expertService.serviceProduct.mpvId,
    };

    // Add designer group option if esMpvId !== caredesign (case insensitive)
    if (expertService.serviceProduct.mpvId.toLowerCase() !== 'caredesign') {
        config.designerGroupOption = {
            key: 'DesignerGroupOption',
            value: 'In-house',
        };
    }

    return config;
}
